import { useState } from 'react';

import type { PaymentRow } from './usePaymentRows';
import type { Dispatch, SetStateAction } from 'react';

interface CardFeeByRowEntry {
    isDisabled?: boolean;
}

interface UseApplyToAllModalsParams {
    paymentRows: PaymentRow[];
    setPaymentRows: Dispatch<SetStateAction<PaymentRow[]>>;
    setPaymentRowErrors: Dispatch<
        SetStateAction<Array<{ bankAccount: boolean; paymentDate: boolean; paymentAmount: boolean }>>
    >;
    setPaymentSubmitErrors: Dispatch<SetStateAction<Array<string | null>>>;
    cardFeeByRow?: CardFeeByRowEntry[];
}

/**
 * Owns the "apply this bank account / card to all selected contracts?" confirmation modals,
 * shown the first time a user picks a bank account or card while multiple rows are present.
 */
export const useApplyToAllModals = ({
    paymentRows,
    setPaymentRows,
    setPaymentRowErrors,
    setPaymentSubmitErrors,
    cardFeeByRow = [],
}: UseApplyToAllModalsParams) => {
    // Clears a row's backend submit-error message once the user edits that row (new card/bank
    // account), since they're now retrying with different input rather than viewing a stale outcome.
    const clearPaymentSubmitError = (index: number) => {
        setPaymentSubmitErrors(prev => {
            if (!prev[index]) return prev;
            const next = [...prev];
            next[index] = null;
            return next;
        });
    };
    const [showBankAccountModal, setShowBankAccountModal] = useState(false);
    const [pendingBankAccount, setPendingBankAccount] = useState<{ rowIndex: number; accountId: string } | null>(null);
    const [hasShownBankAccountModal, setHasShownBankAccountModal] = useState(false);
    const [showApplyCardModal, setShowApplyCardModal] = useState(false);
    const [pendingPaymentCard, setPendingPaymentCard] = useState<{ rowIndex: number; cardId: string } | null>(null);
    const [hasShownApplyCardModal, setHasShownApplyCardModal] = useState(false);

    const handleBankAccountChange = (index: number, value: string) => {
        setPaymentRowErrors(prev => {
            const next = [...prev];
            if (next[index]) next[index] = { ...next[index], bankAccount: false };
            return next;
        });
        clearPaymentSubmitError(index);
        if (!hasShownBankAccountModal && paymentRows.length > 1) {
            setPendingBankAccount({ rowIndex: index, accountId: value });
            setShowBankAccountModal(true);
        } else {
            setPaymentRows(prev => {
                const updated = [...prev];
                updated[index].bankAccount = value;
                return updated;
            });
        }
    };

    const handlePaymentCardChange = (index: number, value: string) => {
        clearPaymentSubmitError(index);
        if (!hasShownApplyCardModal && paymentRows.length > 1) {
            setPendingPaymentCard({ rowIndex: index, cardId: value });
            setShowApplyCardModal(true);
        } else {
            setPaymentRows(prev => {
                const updated = [...prev];
                updated[index].paymentCardId = value;
                return updated;
            });
        }
    };

    const handleCloseBankAccountModal = () => {
        setShowBankAccountModal(false);
        setPendingBankAccount(null);
    };

    const handleCloseApplyCardModal = () => {
        setShowApplyCardModal(false);
        setPendingPaymentCard(null);
    };

    const handleApplyBankAccountToAll = () => {
        if (pendingBankAccount) {
            setPaymentRows(prev =>
                prev.map((row, index) => {
                    // Skip disabled rows (card payment already scheduled or not available for this contract)
                    if (cardFeeByRow[index]?.isDisabled) {
                        return row;
                    }
                    return {
                        ...row,
                        bankAccount: pendingBankAccount.accountId,
                    };
                }),
            );
            setHasShownBankAccountModal(true);
            setPaymentRowErrors(prev => prev.map(e => ({ ...e, bankAccount: false })));
            setPaymentSubmitErrors(prev => prev.map(() => null));
        }
        handleCloseBankAccountModal();
    };

    const handleApplyBankAccountToCurrentOnly = () => {
        if (pendingBankAccount) {
            setPaymentRows(prev => {
                const updated = [...prev];
                updated[pendingBankAccount.rowIndex].bankAccount = pendingBankAccount.accountId;
                return updated;
            });
            setHasShownBankAccountModal(true);
            clearPaymentSubmitError(pendingBankAccount.rowIndex);
        }
        handleCloseBankAccountModal();
    };

    const handleApplyCardToAll = () => {
        if (pendingPaymentCard) {
            setPaymentRows(prev =>
                prev.map((row, index) => {
                    // Skip disabled rows (card payment already scheduled or not available for this contract)
                    if (cardFeeByRow[index]?.isDisabled) {
                        return row;
                    }
                    return {
                        ...row,
                        paymentCardId: pendingPaymentCard.cardId,
                    };
                }),
            );
            setHasShownApplyCardModal(true);
            setPaymentRowErrors(prev => prev.map(e => ({ ...e, bankAccount: false })));
            setPaymentSubmitErrors(prev => prev.map(() => null));
        }
        handleCloseApplyCardModal();
    };

    const handleApplyCardToCurrentOnly = () => {
        if (pendingPaymentCard) {
            clearPaymentSubmitError(pendingPaymentCard.rowIndex);
            setPaymentRows(prev => {
                const updated = [...prev];
                updated[pendingPaymentCard.rowIndex].paymentCardId = pendingPaymentCard.cardId;
                return updated;
            });
            setHasShownApplyCardModal(true);
        }
        handleCloseApplyCardModal();
    };

    return {
        showBankAccountModal,
        handleBankAccountChange,
        handleCloseBankAccountModal,
        handleApplyBankAccountToAll,
        handleApplyBankAccountToCurrentOnly,
        showApplyCardModal,
        handlePaymentCardChange,
        handleCloseApplyCardModal,
        handleApplyCardToAll,
        handleApplyCardToCurrentOnly,
    };
};
